import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Whether the visitor asked the system for reduced motion. Read once per mount, then kept in sync.
 *
 * @returns `true` when motion should be avoided.
 */
export function usePrefersReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(query);
    const update = () => setReduced(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return reduced;
}

/**
 * Feeds the pointer position to CSS, so the stage can light, tilt and parallax with no React re-render.
 *
 * Writes four custom properties on the element, at most once per frame:
 *  - `--mx` / `--my` — the pointer in pixels, relative to the element (the glow's center);
 *  - `--px` / `--py` — the same position normalised to -1…1 from the element's center (tilt, parallax).
 *
 * Does nothing on touch-only devices or when `enabled` is false; the CSS defaults then hold.
 *
 * @param target The element that receives the properties and whose box the position is relative to.
 * @param enabled Turn it off for reduced motion.
 */
export function usePointerField(target: RefObject<HTMLElement>, enabled: boolean): void {
  useEffect(() => {
    const element = target.current;
    if (!element || !enabled) return;
    if (typeof window.matchMedia === 'function' && !window.matchMedia('(pointer: fine)').matches) return;

    let frame = 0;
    let lastX = 0;
    let lastY = 0;

    const paint = () => {
      frame = 0;
      const box = element.getBoundingClientRect();
      const x = lastX - box.left;
      const y = lastY - box.top;
      element.style.setProperty('--mx', `${x.toFixed(1)}px`);
      element.style.setProperty('--my', `${y.toFixed(1)}px`);
      element.style.setProperty('--px', ((x / box.width) * 2 - 1).toFixed(3));
      element.style.setProperty('--py', ((y / box.height) * 2 - 1).toFixed(3));
    };

    const onMove = (event: PointerEvent) => {
      lastX = event.clientX;
      lastY = event.clientY;
      if (!frame) frame = window.requestAnimationFrame(paint);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [target, enabled]);
}
